'use client'

import { useState } from 'react'
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner'
import { cva } from 'class-variance-authority'
import type { ToasterProps } from 'sonner'
import type { VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { cn, warnIfEmptyLabel } from '@/utils'
import { Check, Close, Info } from '@/icons'

const toastVariants = cva('flex items-center border backdrop-opacity-40', {
  variants: {
    type: {
      general: 'bg-gray-200/80 border-gray-300 text-gray-600',
      info: 'bg-support-100/80 border-support-400 text-support-400',
      success: 'bg-positive-100/80 border-positive-400 text-positive-300',
      error: 'bg-danger-100/80 border-danger-400 text-danger-400',
    },
    appearance: {
      floating: 'px-4 py-2 w-full md:min-w-89 rounded-lg',
      banner:
        'w-full rounded-lg p-4 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2',
    },
  },
  defaultVariants: {
    type: 'info',
    appearance: 'floating',
  },
})

const toastIconVariants = cva(
  'rounded-full text-white flex items-center justify-center',
  {
    variants: {
      type: {
        general: 'bg-black',
        info: 'bg-support-200',
        success: 'bg-positive-200',
        error: 'bg-danger-200',
      },
      appearance: {
        floating: 'size-6',
        banner: 'size-8',
      },
    },
    defaultVariants: {
      type: 'info',
      appearance: 'floating',
    },
  },
)

type ToastBaseProps = {
  id?: string | number
  message: string | ReactNode
  icon?: boolean
  button?: {
    text: string
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  }
  onDismiss?: () => void
  className?: string
  ref?: React.Ref<HTMLDivElement>
} & VariantProps<typeof toastVariants>

/**
 * When the toast is dismissible, an accessible name for the dismiss button
 * (`dismissAriaLabel`) becomes required — provide a localized string from the
 * consuming app. A non-dismissible toast renders no dismiss button, so no
 * label is needed.
 */
export type ToastProps = ToastBaseProps &
  (
    | { dismissible?: false; dismissAriaLabel?: never }
    | { dismissible: true; dismissAriaLabel: string }
  )

export const Toast = ({
  id,
  type,
  appearance = 'floating',
  message,
  button,
  icon = false,
  dismissible = false,
  dismissAriaLabel,
  onDismiss,
  className,
  ref,
}: ToastProps) => {
  const [visible, setVisible] = useState(true)
  const [closing, setClosing] = useState(false)

  if (!visible) {
    return null
  }

  const handleDismiss = () => {
    setClosing(true)
  }

  const handleAnimationEnd = () => {
    if (!closing) {
      return
    }
    setVisible(false)
    onDismiss?.()
    if (id != null) {
      sonnerToast.dismiss(id)
    }
  }

  const isBanner = appearance === 'banner'
  const glyphSize = isBanner ? 'size-3.5' : 'size-2.5'

  if (dismissible) {
    warnIfEmptyLabel(dismissAriaLabel, 'Toast: dismissAriaLabel')
  }

  return (
    <div
      ref={ref}
      className={cn(toastVariants({ type, appearance }), className)}
      style={{ backdropFilter: isBanner ? 'blur(10px)' : 'blur(1.3px)' }}
      data-state={closing ? 'closed' : 'open'}
      onAnimationEnd={handleAnimationEnd}
      data-testid="custom-toast-root"
    >
      <div
        className={cn(
          'flex items-center gap-4',
          isBanner ? 'font-medium' : 'font-normal',
        )}
      >
        {icon ? (
          <div className="shrink-0 grow-0 basis-auto">
            <div className={toastIconVariants({ type, appearance })}>
              {type === 'general' || type === 'info' || type == null ? (
                <Info className={glyphSize} />
              ) : null}
              {type === 'success' ? <Check className={glyphSize} /> : null}
              {type === 'error' ? <Close className={glyphSize} /> : null}
            </div>
          </div>
        ) : null}
        <div className={cn('flex items-center', isBanner ? 'gap-4' : 'gap-6')}>
          <p
            className={cn(
              'text-2xs leading-distant font-sans',
              isBanner ? 'font-medium' : 'font-normal',
            )}
          >
            {message}
          </p>

          {button && (
            <button
              onClick={(e) => {
                button.onClick?.(e)
                if (id != null) {
                  sonnerToast.dismiss(id)
                }
              }}
              className="text-2xs leading-distant flex-none cursor-pointer font-sans underline transition-all"
            >
              {button.text}
            </button>
          )}
        </div>
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={dismissAriaLabel}
          className="ml-auto flex-none cursor-pointer transition-all"
          data-testid="custom-toast-dismiss"
        >
          <Close className={isBanner ? 'size-6' : 'size-4'} />
        </button>
      )}
    </div>
  )
}

export const Toaster = (
  props: Pick<
    ToasterProps,
    | 'expand'
    | 'visibleToasts'
    | 'position'
    | 'offset'
    | 'mobileOffset'
    | 'swipeDirections'
    | 'dir'
    | 'hotkey'
    | 'gap'
    | 'className'
    | 'toastOptions'
    | 'duration'
    | 'style'
  >,
) => <SonnerToaster {...props} />
